import React, { FC, useState } from 'react';
import { useTranslation } from 'react-i18next';
import styled, { useTheme } from 'styled-components';

import { POSITION_MARGIN_TABS, POSITION_MARGIN_TYPES, REGEX } from 'src/common/constants';
import { digits, getDecimalPartCount } from 'src/common/helpers';
import {
    Button,
    Modal,
    ModalTitle,
    ModalContent,
    ModalActions,
    Tabs,
    Tab,
    Input,
} from 'src/components/core';
import { Cross } from 'src/components/core/icons';
import { FlexContainer } from 'src/components/core/layout/FlexContainer';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import { modifyPositionMargin } from 'src/setup/redux/slices/derivativesSlice';
import { FutureTradeAble, ModifyPositionMarginPayLoad, Position } from 'src/types';

interface PositionMarginModalProps {
    open: boolean;
    position: Position;
    handleClose: () => void;
}

const StyledModal = styled(Modal)`
    ${({ theme: { colors, typography, breakpoints } }) => `
        .modal-paper {
            border-radius: 8px;
            width: 343px;
            @media (min-width: ${breakpoints.md}) {
                border-radius: 10px;
                width: 429px;
            }
        }
        .modal-title {
            padding: 0px 16px;
            border-bottom: 1px solid ${colors.border.dark};
            .tabs {
                padding: 0;
            }
            .tab {
                padding: 12px 0px;
                &.tab-active {
                    padding: 12px 0px 10px;
                }
            }
        }
        .modal-content {
            padding: 24px 16px 8px;
            .input-container {
                margin-bottom: 0px
            }
            @media (max-width: ${breakpoints.md}) {
            padding: 8px;
        }
        }
        .modal-actions {
            padding: 24px 16px;
            justify-content: center;
            gap: 16px;
            button {
                line-height: calc(${typography.lineHeight[3]} - 0.5px);
                font-weight: ${typography.weight.medium};
                padding: 10px 68px;
                &:nth-of-type(2) {
                    color: ${colors.text.tertiary};
                }
                @media (max-width: ${breakpoints.md}) {
                    justify-content: center;
                    padding: 8px 46px;
                
                }
            }
        }
    `}
`;

const LabelText = styled.div`
    ${({ theme: { colors, typography, breakpoints } }) => `
        font-size: ${typography.size.desktop.paragraph.p2}; 
        color: ${colors.text.tertiary};
        line-height: ${typography.lineHeight[3]};
        width: 70%;
        @media (max-width: ${breakpoints.md}) {
            font-size: ${typography.size.desktop.paragraph.p3}; 
            width: 50%;
        }
    `}
`;

const ValueText = styled.div`
    ${({ theme: { colors, typography, breakpoints } }) => `
        font-size: ${typography.size.desktop.paragraph.p2}; 
        color: ${colors.text.primary};
        line-height: ${typography.lineHeight[3]};
        text-align: left;
        width: 30%;
        @media (max-width: ${breakpoints.md}) {
            font-size: ${typography.size.desktop.paragraph.p3}; 
            width: 50%;
        }
    `}
`;

const PositionMarginModal: FC<PositionMarginModalProps> = ({ open, position, handleClose }) => {
    const { t } = useTranslation();
    const { colors } = useTheme();
    const [activeTab, setActiveTab] = useState(POSITION_MARGIN_TABS.INCREASE_MARGIN);
    const [marginAmount, setMarginAmount] = useState('0');
    const [marginError, setMarginError] = useState('');
    const { tradeable } = useAppSelector((state) => state.derivatives);
    const modifyPositionMarginLoading = useAppSelector(
        (state) => state.derivatives.loading.modifyPositionMargin,
    );
    const futureSymbols = useAppSelector((state) => state.global.configV2.futuresSymbol);
    const { userCurrentFutureTradeAccountIndex } = useAppSelector((state) => state.global);
    const symbolTradeableInfo: FutureTradeAble | undefined = tradeable.find(
        (item) => position.symbolId === item.tokenId,
    );

    const dispatch = useAppDispatch();

    const handleConfirm = () => {
        const positionMarginPayLoad: ModifyPositionMarginPayLoad = {
            symbol_id: position.symbolId,
            is_long: Number(position.isLong),
            amount: Number(marginAmount),
            type:
                POSITION_MARGIN_TABS.INCREASE_MARGIN === activeTab
                    ? POSITION_MARGIN_TYPES.INCREASE
                    : POSITION_MARGIN_TYPES.DECREASE,
            account_index: userCurrentFutureTradeAccountIndex,
        };
        dispatch(modifyPositionMargin(positionMarginPayLoad));
        handleClose();
    };
    const onMarginChange = (val: string) => {
        setMarginError('');
        if (val !== '') {
            if (REGEX.POSITIVE_DECIMAL.test(`${val}`)) {
                setMarginAmount(`${val}`);
            } else {
                setMarginError('Please enter only numbers');
            }
        } else {
            setMarginAmount(`${val}`);
        }
    };

    const getAvailableMargin = () => {
        let availableMargin = 0;
        if (!symbolTradeableInfo) return availableMargin;
        if (activeTab === POSITION_MARGIN_TABS.INCREASE_MARGIN) {
            availableMargin = Number(symbolTradeableInfo.profitLoss.coinAvailable);
        } else {
            availableMargin =
                Number(position.margin) > Number(position.minMargin)
                    ? digits(
                          Number(position.margin) - Number(position.minMargin),
                          getDecimalPartCount(
                              Number(
                                  futureSymbols[position.symbolId]?.baseTokenFutures
                                      ?.marginPrecision,
                              ),
                          ),
                      )
                    : 0;
        }
        return availableMargin;
    };
    return (
        <StyledModal size="md" open={open}>
            <ModalTitle>
                <Tabs
                    className="margin-type"
                    name="marginType"
                    value={activeTab}
                    onChange={setActiveTab}
                >
                    {Object.keys(POSITION_MARGIN_TABS).map((tab, index) => {
                        return (
                            <Tab
                                value={
                                    POSITION_MARGIN_TABS[tab as keyof typeof POSITION_MARGIN_TABS]
                                }
                                key={index}
                                label={t(
                                    POSITION_MARGIN_TABS[tab as keyof typeof POSITION_MARGIN_TABS],
                                )}
                            />
                        );
                    })}
                </Tabs>
                <Cross
                    fill={colors.text.primary}
                    onClick={handleClose}
                    className="clickable"
                    style={{ height: 20, width: 20 }}
                />
            </ModalTitle>
            <ModalContent>
                <FlexContainer
                    styles={{
                        flexDirection: 'column',
                        gap: 16,
                    }}
                >
                    <FlexContainer
                        styles={{
                            width: '100%',
                            justifyContent: 'space-between',
                            gap: 16,
                        }}
                    >
                        <LabelText>{t('contract')}</LabelText>
                        <ValueText>{position.symbolName}</ValueText>
                    </FlexContainer>
                    <FlexContainer
                        styles={{
                            width: '100%',
                            justifyContent: 'space-between',
                            gap: 16,
                        }}
                    >
                        <LabelText>{t('position(cont)')}</LabelText>
                        <ValueText>{position.available}</ValueText>
                    </FlexContainer>
                    <FlexContainer
                        styles={{
                            width: '100%',
                            justifyContent: 'space-between',
                            gap: 16,
                        }}
                    >
                        <LabelText>{`${t('position_margin')} (${position.unit})`}</LabelText>
                        <ValueText>{position.margin}</ValueText>
                    </FlexContainer>
                    <FlexContainer
                        styles={{
                            width: '100%',
                            justifyContent: 'space-between',
                            alignItems: 'start',
                            gap: 16,
                        }}
                    >
                        <LabelText>
                            {`${t(`max_allowed_${activeTab}`)} (${position.unit})`}
                        </LabelText>
                        <ValueText>{getAvailableMargin()}</ValueText>
                    </FlexContainer>
                    <FlexContainer
                        styles={{
                            width: '100%',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            gap: 16,
                        }}
                    >
                        <LabelText>{t(activeTab)}</LabelText>
                        <ValueText>
                            <Input
                                name={'margin'}
                                hasTooltip={false}
                                fullWidth
                                value={marginAmount}
                                className={'margin-amount-input'}
                                error={marginError}
                                onChange={(val) => onMarginChange(val.toString())}
                            />
                        </ValueText>
                    </FlexContainer>
                </FlexContainer>
            </ModalContent>
            <ModalActions>
                <Button onClick={handleConfirm} fullWidth loading={modifyPositionMarginLoading}>
                    {t('submit')}
                </Button>
                <Button variant="outlined" onClick={handleClose} fullWidth>
                    {t('cancel')}
                </Button>
            </ModalActions>
        </StyledModal>
    );
};

export { PositionMarginModal };
